import { IChartApi, MouseEventParams, Time } from 'lightweight-charts';
import { useEffect, useRef } from 'react';
import { useChartContext } from '../components/chart.context';
import {
  attachCrosshairElements,
  createCrosshairElements,
  CrosshairElements,
  removeCrosshairElements,
  updateCrosshairPosition
} from './utils/crosshair.utils';
import {
  getCrosshairPosition$,
  updateCrosshairPosition as updateGlobalCrosshair,
  getCrosshairSyncInstanceId
} from '../services/global-crosshair-sync.service';
import {
  adjustTimeForTimeframe,
  ChartTime,
  needsTimeAdjustment,
  resolveXPos,
  safeTimeToCoordinate,
  toSecondsIfMs
} from '../services/timeframe-utils';
import { useHorizontalScrollSynchronization } from './use-horizontal-scroll-synchronization.hook';
import { useObservable } from '@client/foundation/shared/utils/observable.hook';
import { getChartSyncEnabled$ } from '../services/chart-sync-toggle.service';
import { toNumericTimeOrNull } from './utils/time-normalization.utils';

export const useChartSynchronization = (
  timeframeIndex?: number,
  timeframe?: string,
  centers?: {
    centerChartsOnTime: (time: Time) => void;
    centerChartsOnLogical: (logicalPosition: number) => void;
  }
) => {
  const { chartRef, indicatorChartRefs, chartInitialized } = useChartContext();
  const crosshairRef = useRef<CrosshairElements[]>([]);
  const { centerChartsOnTime, centerChartsOnLogical } =
    centers ??
    useHorizontalScrollSynchronization({
      // Disable range listeners here to avoid duplicating the group sync logic.
      syncOnRangeChange: false
    });
  const chartSyncEnabled = useObservable(getChartSyncEnabled$, true);

  useEffect(() => {
    if (!chartInitialized) return;

    const unsubscribes: (() => void)[] = [];
    let isSyncing = false;

    const customCrosshairElements = new Map<
      IChartApi,
      ReturnType<typeof createCrosshairElements>
    >();

    const rafId = requestAnimationFrame(() => {
      const charts = [
        chartRef.current,
        ...Object.values(indicatorChartRefs.current).flat()
      ].filter(Boolean) as IChartApi[];

      // Initialize charts
      charts.forEach((chart, index) => {
        chart.applyOptions({
          crosshair: {
            mode: 0,
            vertLine: { visible: false },
            horzLine: { visible: true }
          }
        });

        const elements =
          crosshairRef.current[index] ?? createCrosshairElements();

        // Ensure elements are attached and tracked even after a resync
        attachCrosshairElements(chart, elements);
        customCrosshairElements.set(chart, elements);
        crosshairRef.current[index] = elements;
      });

      // Local crosshair synchronization (within this timeframe group)
      charts.forEach((chart) => {
        const handleCrosshairMove = (param: MouseEventParams<Time>) => {
          if (isSyncing) return;
          isSyncing = true;

          requestAnimationFrame(() => {
            const t: ChartTime = param?.time ?? null;

            charts.forEach((otherChart) => {
              const elements = customCrosshairElements.get(otherChart);
              if (!elements) return;

              // For local sync we do NOT need needsTimeAdjustment; they are same timeframe group.
              const { x, usedTime } = resolveXPos(otherChart, t, timeframe);

              // If sync disabled, only update the chart that produced the move
              if (!chartSyncEnabled && otherChart !== chart) return;

              updateCrosshairPosition(
                elements,
                x,
                toNumericTimeOrNull(usedTime ?? t) ?? undefined
              );
            });

            // Always emit globally so other processes can listen, regardless of local toggle
            updateGlobalCrosshair({
              time: toNumericTimeOrNull(t),
              sourceTimeframeIndex: timeframeIndex,
              sourceTimeframe: timeframe
            });

            isSyncing = false;
          });
        };

        chart.subscribeCrosshairMove(handleCrosshairMove);
        unsubscribes.push(() =>
          chart.unsubscribeCrosshairMove(handleCrosshairMove)
        );
      });

      // Global crosshair synchronization (between timeframes)
      const globalCrosshairSubscription = getCrosshairPosition$().subscribe(
        (data) => {
          const isSameInstance =
            data.sourceInstanceId === getCrosshairSyncInstanceId();

          const isOwnTimeframeOnThisInstance =
            isSameInstance && data.sourceTimeframeIndex === timeframeIndex;

          if (isOwnTimeframeOnThisInstance || isSyncing || !chartSyncEnabled)
            return;

          requestAnimationFrame(() => {
            let timeToCenter: ChartTime = null;

            charts.forEach((chart) => {
              const elements = customCrosshairElements.get(chart);
              if (!elements) return;

              let t: ChartTime = data.time;

              // If TF differs, adjust (round) first
              if (
                typeof t === 'number' &&
                data.sourceTimeframe &&
                timeframe &&
                needsTimeAdjustment(data.sourceTimeframe, timeframe)
              ) {
                // NOTE: your adjustTimeForTimeframe is target-only; that's fine for rounding.
                t = adjustTimeForTimeframe(
                  toSecondsIfMs(t) as number,
                  timeframe
                );
              } else {
                t = toSecondsIfMs(t);
              }

              const { x, usedTime } =
                typeof t === 'number'
                  ? resolveXPos(chart, t, timeframe)
                  : { x: safeTimeToCoordinate(chart, t) };

              if (timeToCenter === null) {
                timeToCenter = (usedTime ?? t) as ChartTime;
              }

              updateCrosshairPosition(
                elements,
                x,
                toNumericTimeOrNull(usedTime ?? t) ?? undefined
              );
            });

            const numericTime = toNumericTimeOrNull(timeToCenter);
            if (numericTime !== null && chartRef.current) {
              const { x } = resolveXPos(
                chartRef.current,
                numericTime,
                timeframe
              );

              const logical =
                x !== null
                  ? chartRef.current
                      .timeScale()
                      .coordinateToLogical(x as number)
                  : null;

              if (logical !== null) {
                centerChartsOnLogical(logical as number);
              } else {
                centerChartsOnTime(numericTime as Time);
              }
            }
          });
        }
      );

      unsubscribes.push(() => globalCrosshairSubscription.unsubscribe());
    });

    return () => {
      cancelAnimationFrame(rafId);
      unsubscribes.forEach((u) => u());
      customCrosshairElements.forEach((elements, chart) => {
        removeCrosshairElements(chart, elements);
      });
      // Reset cached refs so re-sync can recreate/reattach as needed
      crosshairRef.current = [];
    };
  }, [
    chartInitialized,
    chartSyncEnabled,
    timeframeIndex,
    timeframe,
    // rerun when subplot count changes (charts recreated)
    Object.keys(indicatorChartRefs.current).length
  ]);
};
